import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Heading } from "@tiptap/extension-heading";
import { Link } from "@tiptap/extension-link";
import { ResizableImage } from "./ResizableImage";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { FontSize } from "tiptap-extension-font-size";
import OfficePaste from "@intevation/tiptap-extension-office-paste";
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Tab,
  Tabs,
  Button,
  Popover,
} from "@mui/material";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import LinkIcon from "@mui/icons-material/Link";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useRef, useState } from "react";

// --- Types ---
interface SignatureEditorProps {
  value: string;
  onChange: (html: string) => void;
}

const FONT_FAMILIES = [
  "Aptos",
  "Arial",
  "Calibri",
  "Times New Roman",
  "Georgia",
  "Courier New",
  "Verdana",
  "Trebuchet MS",
];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];
const MAX_IMAGE_WIDTH = 600;

// --- Colour swatch button (matches Outlook's A with coloured bar) ---
const ColorSwatchButton = ({
  label,
  title,
  currentColor,
  inputRef,
  onChange,
}: {
  label: string;
  title: string;
  currentColor: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (color: string) => void;
}) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onClick={() => inputRef.current?.click()}
      sx={{
        px: 0.75,
        py: 0.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        borderRadius: 1,
        minWidth: 28,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.2,
          color: "#1f1f1f",
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          width: 16,
          height: 3,
          borderRadius: "1px",
          bgcolor: currentColor,
          mt: "1px",
        }}
      />
      <input
        ref={inputRef}
        type="color"
        defaultValue={
          currentColor === "inherit" || currentColor === ""
            ? "#000000"
            : currentColor
        }
        style={{
          position: "absolute",
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: "none",
        }}
        onChange={(e) => onChange(e.target.value)}
      />
    </IconButton>
  </Tooltip>
);

// --- Insert tab button (icon + label, like Outlook) ---
const InsertButton = ({
  icon,
  label,
  title,
  onClick,
  active = false,
}: {
  icon: React.ReactNode;
  label: string;
  title: string;
  onClick: () => void;
  active?: boolean;
}) => (
  <Tooltip title={title}>
    <Button
      onClick={onClick}
      size="small"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        px: 2,
        py: 0.75,
        minWidth: 72,
        textTransform: "none",
        color: active ? "#0078d4" : "#333",
        bgcolor: "transparent",
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.3,
        borderRadius: 1,
        border: "none",
        boxShadow: "none",
        "&:hover": { bgcolor: "#f0f0f0", boxShadow: "none" },
      }}
    >
      {icon}
      <span>{label}</span>
    </Button>
  </Tooltip>
);

// --- Table management action button ---
const TableActionButton = ({
  icon,
  label,
  title,
  onClick,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  title: string;
  onClick: () => void;
  danger?: boolean;
}) => (
  <Tooltip title={title}>
    <Button
      size="small"
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
        px: 1.5,
        py: 0.5,
        minWidth: 56,
        textTransform: "none",
        color: danger ? "#c0392b" : "#333",
        bgcolor: "transparent",
        fontSize: 10,
        fontWeight: 400,
        lineHeight: 1.3,
        borderRadius: 1,
        border: "none",
        boxShadow: "none",
        "&:hover": {
          bgcolor: danger ? "#ffeaea" : "#f0f0f0",
          boxShadow: "none",
        },
      }}
    >
      {icon}
      <span>{label}</span>
    </Button>
  </Tooltip>
);

// --- Outlook-style hover grid table picker ---
const GRID_ROWS = 8;
const GRID_COLS = 8;
const TableGridPicker = ({
  onSelect,
}: {
  onSelect: (rows: number, cols: number) => void;
}) => {
  const [hovered, setHovered] = useState({ row: 0, col: 0 });
  return (
    <Box sx={{ p: 1.5, userSelect: "none" }}>
      <Box
        sx={{
          fontSize: 12,
          color: "#444",
          mb: 1,
          textAlign: "center",
          minHeight: 18,
        }}
      >
        {hovered.row > 0
          ? `${hovered.row} × ${hovered.col} Table`
          : "Insert table"}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_COLS}, 22px)`,
          gap: "3px",
        }}
        onMouseLeave={() => setHovered({ row: 0, col: 0 })}
      >
        {Array.from({ length: GRID_ROWS }).map((_, r) =>
          Array.from({ length: GRID_COLS }).map((_, c) => (
            <Box
              key={`${r}-${c}`}
              onMouseEnter={() => setHovered({ row: r + 1, col: c + 1 })}
              onClick={() => onSelect(r + 1, c + 1)}
              sx={{
                width: 22,
                height: 22,
                border: "1px solid",
                borderColor:
                  r < hovered.row && c < hovered.col ? "#0078d4" : "#ccc",
                bgcolor:
                  r < hovered.row && c < hovered.col ? "#dbeafe" : "#fff",
                cursor: "pointer",
                borderRadius: "2px",
                transition: "background-color 0.05s, border-color 0.05s",
              }}
            />
          )),
        )}
      </Box>
    </Box>
  );
};

// Preserves full inline style + class on <table> (e.g. pasted email signatures).
const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class") || null,
        renderHTML: (attributes) => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
    };
  },
});

const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const StyleParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const StyleHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
}).configure({ levels: [1, 2, 3, 4, 5, 6] });

// Preserves ALL inline styles on <td> and <th> cells (background-color, color,
// padding, width, etc.). Without this TipTap silently strips the style attribute
// because it is not in the default TableCell/TableHeader schema.
const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style") || null,
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
    };
  },
});

// --- Main component ---
export const SignatureEditor = ({ value, onChange }: SignatureEditorProps) => {
  const textColorRef = useRef<HTMLInputElement>(null);
  const highlightColorRef = useRef<HTMLInputElement>(null);
  const initialised = useRef(false);
  const [activeTab, setActiveTab] = useState<"format" | "insert">("format");
  const [inTable, setInTable] = useState(false);
  const [tableGridAnchor, setTableGridAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [toolbarAttrs, setToolbarAttrs] = useState({
    fontFamily: "Aptos",
    fontSize: "12",
    textColor: "#000000",
    highlight: "#ffff00",
  });

  const syncToolbarAttrs = (ed: typeof editor) => {
    if (!ed) return;
    setToolbarAttrs({
      fontFamily: ed.getAttributes("textStyle").fontFamily || "Aptos",
      fontSize:
        ed.getAttributes("textStyle").fontSize?.replace("px", "") || "12",
      textColor: ed.getAttributes("textStyle").color || "#000000",
      highlight: ed.getAttributes("highlight").color || "#ffff00",
    });
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        paragraph: false,
        heading: false,
      }),
      StyleParagraph,
      StyleHeading,
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["paragraph", "heading"], defaultAlignment: "" }),
      CustomTable.configure({ resizable: true }),
      CustomTableRow,
      CustomTableHeader,
      CustomTableCell,
      ResizableImage.configure({ inline: true, allowBase64: true }),
      Link.configure({ openOnClick: false }),
      FontSize as any,
      OfficePaste,
    ],
    editorProps: {
      transformPastedHTML(html) {
        return html.replace(/\btext-align\s*:\s*left\s*;?\s*/gi, "");
      },
    },
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
      syncToolbarAttrs(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      setInTable(
        editor.isActive("tableCell") || editor.isActive("tableHeader"),
      );
      syncToolbarAttrs(editor);
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (!initialised.current && value) {
      editor.commands.setContent(value, { emitUpdate: false });
      initialised.current = true;
    }
  }, [editor, value]);

  const handleImageInsert = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const scale =
            img.width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH / img.width : 1;
          const canvas = document.createElement("canvas");
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas
            .getContext("2d")!
            .drawImage(img, 0, 0, canvas.width, canvas.height);
          editor
            ?.chain()
            .focus()
            .setImage({
              src: canvas.toDataURL("image/png"),
              width: canvas.width,
              height: canvas.height,
            } as any)
            .run();
        };
        img.src = e.target!.result as string;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleLinkInsert = () => {
    const url = window.prompt("Enter URL:");
    if (!url) return;
    editor?.chain().focus().setLink({ href: url }).run();
  };

  if (!editor) return null;

  const currentFont = toolbarAttrs.fontFamily;
  const currentFontSize = toolbarAttrs.fontSize;
  const currentTextColor = toolbarAttrs.textColor;
  const currentHighlight = toolbarAttrs.highlight;

  return (
    <Box
      sx={{
        border: "1px solid #d0d0d0",
        borderRadius: "4px",
        overflow: "hidden",
        fontFamily: "Aptos, Arial, sans-serif",
        bgcolor: "#fff",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:focus-within": {
          borderColor: "#0078d4",
          boxShadow: "0 0 0 2px rgba(0,120,212,0.18)",
        },
      }}
    >
      {/* Tab bar — matches Outlook's "Format text | Insert" ribbon tabs */}
      <Box sx={{ borderBottom: "1px solid #e0e0e0", bgcolor: "#fff" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            px: 1,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0,
              px: 1.5,
              fontSize: 13,
              fontWeight: 400,
              textTransform: "none",
              color: "#444",
              "&.Mui-selected": { color: "#0078d4", fontWeight: 600 },
            },
            "& .MuiTabs-indicator": { bgcolor: "#0078d4", height: 2 },
          }}
        >
          <Tab value="format" label="Format text" disableRipple />
          <Tab value="insert" label="Insert" disableRipple />
        </Tabs>
      </Box>

      {/* Toolbar content */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.25,
          px: 1,
          py: 0.5,
          bgcolor: "#fff",
          borderBottom: "1px solid #e8e8e8",
          minHeight: 40,
        }}
      >
        {activeTab === "format" && (
          <>
            {/* Undo */}
            <Tooltip title="Undo">
              <span>
                <IconButton
                  size="small"
                  onClick={() => editor.chain().focus().undo().run()}
                  disabled={!editor.can().undo()}
                  sx={{ borderRadius: 1 }}
                >
                  <UndoIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.5 }}
            />

            {/* Font family */}
            <Select
              size="small"
              value={currentFont}
              onChange={(e) =>
                editor.chain().focus().setFontFamily(e.target.value).run()
              }
              sx={{
                fontSize: 13,
                height: 28,
                minWidth: 120,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "& .MuiSelect-select": { py: 0.5, px: 1 },
              }}
            >
              {FONT_FAMILIES.map((f) => (
                <MenuItem
                  key={f}
                  value={f}
                  sx={{ fontFamily: f, fontSize: 13 }}
                >
                  {f}
                </MenuItem>
              ))}
            </Select>

            {/* Font size */}
            <Select
              size="small"
              value={currentFontSize}
              onChange={(e) => {
                const val = e.target.value;
                if (val)
                  (editor.chain().focus() as any).setFontSize(`${val}px`).run();
              }}
              sx={{
                fontSize: 13,
                height: 28,
                minWidth: 56,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "& .MuiSelect-select": { py: 0.5, px: 1 },
              }}
            >
              {FONT_SIZES.map((s) => (
                <MenuItem key={s} value={String(s)} sx={{ fontSize: 13 }}>
                  {s}
                </MenuItem>
              ))}
            </Select>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.5 }}
            />

            {/* B I U S */}
            <Tooltip title="Bold (Ctrl+B)">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBold().run()}
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive("bold") ? "#e8f0fe" : "transparent",
                  fontWeight: 700,
                  fontSize: 14,
                  width: 28,
                  height: 28,
                }}
              >
                <strong style={{ fontSize: 14 }}>B</strong>
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic (Ctrl+I)">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive("italic")
                    ? "#e8f0fe"
                    : "transparent",
                  width: 28,
                  height: 28,
                }}
              >
                <em
                  style={{
                    fontSize: 14,
                    fontStyle: "italic",
                    fontFamily: "serif",
                  }}
                >
                  I
                </em>
              </IconButton>
            </Tooltip>
            <Tooltip title="Underline (Ctrl+U)">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive("underline")
                    ? "#e8f0fe"
                    : "transparent",
                  width: 28,
                  height: 28,
                }}
              >
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    textDecoration: "underline",
                    lineHeight: 1,
                  }}
                >
                  U
                </Box>
              </IconButton>
            </Tooltip>
            <Tooltip title="Strikethrough">
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive("strike")
                    ? "#e8f0fe"
                    : "transparent",
                  width: 28,
                  height: 28,
                }}
              >
                <StrikethroughSIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.5 }}
            />

            {/* Highlight colour */}
            <ColorSwatchButton
              label="A"
              title="Highlight colour"
              currentColor={currentHighlight}
              inputRef={highlightColorRef}
              onChange={(color) =>
                editor.chain().focus().setHighlight({ color }).run()
              }
            />

            {/* Text colour */}
            <ColorSwatchButton
              label="A"
              title="Font colour"
              currentColor={currentTextColor}
              inputRef={textColorRef}
              onChange={(color) => editor.chain().focus().setColor(color).run()}
            />

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.5 }}
            />

            {/* Alignment */}
            <Tooltip title="Align left">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive({ textAlign: "left" })
                    ? "#e8f0fe"
                    : "transparent",
                  width: 28,
                  height: 28,
                }}
              >
                <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align centre">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive({ textAlign: "center" })
                    ? "#e8f0fe"
                    : "transparent",
                  width: 28,
                  height: 28,
                }}
              >
                <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Align right">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                sx={{
                  borderRadius: 1,
                  bgcolor: editor.isActive({ textAlign: "right" })
                    ? "#e8f0fe"
                    : "transparent",
                  width: 28,
                  height: 28,
                }}
              >
                <FormatAlignRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 0.5 }}
            />

            {/* Clear formatting */}
            <Tooltip title="Clear formatting">
              <IconButton
                size="small"
                onClick={() =>
                  editor.chain().focus().unsetAllMarks().clearNodes().run()
                }
                sx={{ borderRadius: 1, width: 28, height: 28 }}
              >
                <FormatClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {activeTab === "insert" && (
          <>
            <InsertButton
              icon={<LinkIcon sx={{ fontSize: 22 }} />}
              label="Link"
              title="Insert link"
              onClick={handleLinkInsert}
              active={editor.isActive("link")}
            />
            <InsertButton
              icon={<InsertPhotoIcon sx={{ fontSize: 22 }} />}
              label="Pictures"
              title="Insert image"
              onClick={handleImageInsert}
            />

            {/* Table button opens hover grid picker */}
            <Tooltip title="Insert table">
              <Button
                size="small"
                onClick={(e) => setTableGridAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  px: 2,
                  py: 0.75,
                  minWidth: 72,
                  textTransform: "none",
                  color: "#333",
                  bgcolor: "transparent",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.3,
                  borderRadius: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#f0f0f0", boxShadow: "none" },
                }}
              >
                <TableChartIcon sx={{ fontSize: 22 }} />
                <span>Table</span>
              </Button>
            </Tooltip>
            <Popover
              open={Boolean(tableGridAnchor)}
              anchorEl={tableGridAnchor}
              onClose={() => setTableGridAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: 3 } } }}
            >
              <TableGridPicker
                onSelect={(rows, cols) => {
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows, cols, withHeaderRow: false })
                    .run();
                  setTableGridAnchor(null);
                }}
              />
            </Popover>

            {/* Table management — shown only when cursor is inside a table */}
            {inTable && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 0.5, my: 0.5 }}
                />
                <TableActionButton
                  icon={<TableRowsIcon sx={{ fontSize: 18 }} />}
                  label="Row ↑"
                  title="Add row above"
                  onClick={() => editor.chain().focus().addRowBefore().run()}
                />
                <TableActionButton
                  icon={<TableRowsIcon sx={{ fontSize: 18 }} />}
                  label="Row ↓"
                  title="Add row below"
                  onClick={() => editor.chain().focus().addRowAfter().run()}
                />
                <TableActionButton
                  icon={<DeleteIcon sx={{ fontSize: 18 }} />}
                  label="Del Row"
                  title="Delete row"
                  onClick={() => editor.chain().focus().deleteRow().run()}
                  danger
                />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 0.5, my: 0.5 }}
                />
                <TableActionButton
                  icon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
                  label="Col ←"
                  title="Add column before"
                  onClick={() => editor.chain().focus().addColumnBefore().run()}
                />
                <TableActionButton
                  icon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
                  label="Col →"
                  title="Add column after"
                  onClick={() => editor.chain().focus().addColumnAfter().run()}
                />
                <TableActionButton
                  icon={<DeleteIcon sx={{ fontSize: 18 }} />}
                  label="Del Col"
                  title="Delete column"
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                  danger
                />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 0.5, my: 0.5 }}
                />
                <TableActionButton
                  icon={<DeleteIcon sx={{ fontSize: 18 }} />}
                  label="Del Table"
                  title="Delete table"
                  onClick={() => editor.chain().focus().deleteTable().run()}
                  danger
                />
              </>
            )}
          </>
        )}
      </Box>

      {/* Editor writing area */}
      <Box
        sx={{
          bgcolor: "#fff",
          "& .tiptap": {
            outline: "none",
            minHeight: 220,
            padding: "12px 16px",
            fontSize: "12pt",
            color: "#1f1f1f",
            "& > p, & > ul, & > ol, & > blockquote, & > pre, & > h1, & > h2, & > h3, & > h4, & > h5, & > h6": {
              fontFamily: "Aptos, Arial, sans-serif",
              lineHeight: 1.5,
            },
            "& > p": { margin: "0 0 12px" },
            "& table td p, & table th p": {
              margin: 0,
              marginBottom: 0,
              lineHeight: "inherit",
            },
            // Descendant (not direct-child) selector — a <ul> nested inside an <li> is not a
            // direct child of the editor root, so "& > ul" alone never matches it, leaving
            // sub-bullets with no indentation at all.
            "& ul, & ol": { paddingLeft: "1.5em", margin: "0 0 8px" },
            "& table td ul, & table td ol, & table th ul, & table th ol": {
              margin: 0,
              marginBottom: 0,
            },
            "& a": { color: "#0078d4", textDecoration: "underline" },
            "& u": { textDecoration: "underline !important" },
            "& s": { textDecoration: "line-through !important" },
            "& img": {
              maxWidth: "100%",
              verticalAlign: "bottom",
            },
            "& img:not([height]):not([style*='height']):not([style*='Height'])": {
              height: "auto",
            },
            "& img[style*='width']": { maxWidth: "none" },
            "& table": {
              borderCollapse: "collapse",
              width: "auto",
              maxWidth: "100%",
            },
            "& td, & th": {
              border: "none",
              verticalAlign: "top",
              boxSizing: "border-box",
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Table styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `

        .tiptap table { border-collapse: collapse; table-layout: auto; margin: 0; width: auto; max-width: 100%; overflow: visible; }
        .tiptap td, .tiptap th { min-width: 1em; border: none; vertical-align: top; box-sizing: border-box; position: relative; }

        .tiptap .selectedCell:after { z-index: 2; position: absolute; content: ""; left: 0; right: 0; top: 0; bottom: 0; background: rgba(0,120,212,0.12); pointer-events: none; }
        .tiptap .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px; background-color: #0078d4; pointer-events: none; }
      `,
        }}
      />
    </Box>
  );
};
